import { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Ban, LogOut, ShieldCheck, Trash2 } from "lucide-react";
import { api, errMsg, fmtDateTime, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const run = async (fn, ok, reload) => {
  try {
    await fn();
    toast.success(ok);
    reload?.();
  } catch (e) {
    toast.error(errMsg(e));
  }
};
const LV = ["L1", "L2", "L3", "L4", "L5"];

function UserRow({ u, reload, superMode }) {
  const [hist, setHist] = useState(null);
  const { user: me } = useAuth();
  const canDelete = u.role !== "super_admin" && u.user_id !== me?.user_id && (u.role !== "admin" || me?.role === "super_admin");
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`authz-user-${u.user_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-800">{u.name} <span className="text-xs font-normal text-slate-500">{u.email}</span></p>
          <p className="text-xs text-slate-500">Metodi: {u.levels.providers.join(", ") || "-"} · Prof.: {u.levels.professional_status || "-"} · Sessioni attive: {u.active_sessions} · MFA: {u.mfa_enabled ? "sì" : "no"} · Stato: <b className={u.status === "suspended" ? "text-rose-600" : "text-emerald-700"}>{u.status === "suspended" ? "sospeso" : "attivo"}</b></p>
          <div className="mt-1 flex flex-wrap gap-1">{LV.map((l) => <span key={l} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${u.levels[l] ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{l}{l === "L2" && u.levels.L2_sandbox ? "·sb" : ""}</span>)}</div>
          <p className="mt-1 text-[11px] text-slate-400">Permessi: {u.permissions.join(", ")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={u.role} onValueChange={(r) => run(() => api.put(`/admin/users/${u.user_id}`, { role: r }), "Ruolo aggiornato (sessioni revocate)", reload)}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid={`authz-role-${u.user_id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(ROLE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" data-testid={`authz-suspend-${u.user_id}`} onClick={() => run(() => api.post(`/admin/authz/users/${u.user_id}/suspend`, null, { params: { suspend: u.status !== "suspended" } }), "Stato aggiornato", reload)}><Ban className="mr-1 h-3.5 w-3.5" />{u.status === "suspended" ? "Riattiva" : "Sospendi"}</Button>
          <Button size="sm" variant="outline" data-testid={`authz-revoke-${u.user_id}`} onClick={() => run(() => api.post(`/admin/authz/users/${u.user_id}/revoke-sessions`), "Sessioni revocate", reload)}><LogOut className="mr-1 h-3.5 w-3.5" />Revoca sessioni</Button>
          <Button size="sm" variant="ghost" data-testid={`authz-history-${u.user_id}`} onClick={async () => setHist(hist ? null : (await api.get(`/admin/authz/users/${u.user_id}/history`)).data)}><History className="mr-1 h-3.5 w-3.5" />Storico</Button>
          {canDelete && superMode && <Button size="sm" variant="outline" data-testid={`authz-anonymize-${u.user_id}`} className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => window.confirm(`Eliminare ${u.email}? Nome, email e accesso verranno cancellati; prenotazioni e documenti sanitari restano conservati in forma anonima. L'operazione non si può annullare.`) && run(() => api.delete(`/superadmin/users/${u.user_id}`), "Account eliminato (anonimizzato)", reload)}><Trash2 className="mr-1 h-3.5 w-3.5" />Elimina</Button>}
          {canDelete && !superMode && <Button size="sm" variant="outline" data-testid={`authz-delete-${u.user_id}`} className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => window.confirm(`Eliminare definitivamente ${u.email}? Verranno cancellati account, prenotazioni, documenti e storico. L'operazione non si può annullare.`) && run(() => api.delete(`/admin/users/${u.user_id}`), "Utente eliminato definitivamente", reload)}><Trash2 className="mr-1 h-3.5 w-3.5" />Elimina</Button>}
        </div>
      </div>
      {hist && <div className="mt-3 max-h-40 overflow-y-auto rounded-lg bg-slate-50 p-2 font-mono text-[11px] text-slate-600">{hist.map((h) => <p key={h.id}>{fmtDateTime(h.at)} · {h.action} · {h.actor_email || "sistema"} {h.detail}</p>)}</div>}
    </div>
  );
}

const isPro = (u) => ["medico", "infermiere"].includes(u.role) || !!u.levels.professional_status;

export function AuthzUsers({ superMode = false }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("tutti");
  const load = () => api.get("/admin/authz/users").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  const shown = items.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()) && (kind === "tutti" || (kind === "pro") === isPro(u)) && !u.erased_at);
  return (
    <div className="space-y-3" data-testid="authz-users">
      <div className="flex flex-wrap gap-2">
        <Input data-testid="authz-search" placeholder="Cerca utente…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
        <Select value={kind} onValueChange={setKind}><SelectTrigger className="w-48" data-testid="authz-kind-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="tutti">Tutti</SelectItem><SelectItem value="utenti">Solo utenti</SelectItem><SelectItem value="pro">Solo professionisti</SelectItem></SelectContent></Select>
      </div>
      {shown.map((u) => <UserRow key={u.user_id} u={u} reload={load} superMode={superMode} />)}
    </div>
  );
}

export function Verifications({ superMode = false }) {
  const [items, setItems] = useState([]);
  const [note, setNote] = useState({});
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/verifications").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  const decide = (v, status, assign_role) => run(() => api.post(`/admin/verifications/${v.id}/decision`, { status, note: note[v.id] || "", valid_months: 12, assign_role }), `Stato: ${status}`, load);
  const openDoc = async (v, d) => {
    const res = await api.get(`/admin/verifications/${v.id}/document/${d.id}`, { responseType: "blob" });
    window.open(URL.createObjectURL(res.data));
  };
  const remove = (v) => window.confirm(`Eliminare ${v.user_email}? Nome, email e accesso verranno cancellati; prenotazioni e documenti sanitari restano conservati in forma anonima. L'operazione non si può annullare.`)
    && run(() => api.delete(`/superadmin/users/${v.user_id}`), "Utente eliminato (anonimizzato)", load);
  const term = q.trim().toLowerCase();
  const shown = items.filter((v) => !term || [v.user_email, v.credential.first_name, v.credential.last_name, v.credential.profession, v.credential.codice_fiscale, v.credential.albo_number, v.status]
    .filter(Boolean).join(" ").toLowerCase().includes(term));
  return (
    <div className="space-y-3" data-testid="verifications-list">
      <Input data-testid="verifications-search" className="max-w-md" placeholder="Cerca per nome, email, professione, codice fiscale, albo o stato" value={q} onChange={(e) => setQ(e.target.value)} />
      {!items.length && <p className="text-sm text-slate-500">Nessuna richiesta di verifica.</p>}
      {items.length > 0 && !shown.length && <p className="text-sm text-slate-500" data-testid="verifications-no-results">Nessun professionista corrisponde alla ricerca.</p>}
      {shown.map((v) => (
        <div key={v.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`verification-${v.id}`}>
          <p className="text-sm font-semibold">{v.credential.first_name} {v.credential.last_name} · {v.credential.profession} · <span className="text-sky-700">{v.status}</span></p>
          <p className="text-xs text-slate-500">{v.user_email} · CF {v.credential.codice_fiscale} · {v.credential.degree} · Albo: {v.credential.albo_registered ? `${v.credential.albo_number} (${v.credential.order_name})` : "no"} · Scadenza: {v.expires_at ? fmtDateTime(v.expires_at) : "-"}</p>
          <div className="mt-1 flex flex-wrap gap-2">{v.documents.map((d) => <button key={d.id} onClick={() => openDoc(v, d)} className="text-xs text-sky-700 underline">{d.filename}</button>)}</div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Input className="h-8 max-w-xs text-xs" placeholder="Nota" value={note[v.id] || ""} onChange={(e) => setNote({ ...note, [v.id]: e.target.value })} />
            <Button size="sm" variant="outline" data-testid={`verification-review-${v.id}`} onClick={() => decide(v, "UNDER_REVIEW")}>In revisione</Button>
            <Button size="sm" data-testid={`verification-approve-${v.id}`} onClick={() => decide(v, "VERIFIED", v.credential.profession === "medico" ? "medico" : "infermiere")} className="bg-emerald-600 hover:bg-emerald-700"><ShieldCheck className="mr-1 h-3.5 w-3.5" />Verifica e assegna ruolo</Button>
            <Button size="sm" variant="outline" data-testid={`verification-reject-${v.id}`} onClick={() => decide(v, "REJECTED")}>Rifiuta</Button>
            <Button size="sm" variant="outline" data-testid={`verification-suspend-${v.id}`} onClick={() => decide(v, "SUSPENDED")}>Sospendi</Button>
            <Button size="sm" variant="outline" data-testid={`verification-expire-${v.id}`} onClick={() => decide(v, "EXPIRED")}>Scaduta</Button>
            {superMode && <Button size="sm" variant="outline" data-testid={`verification-delete-${v.id}`} className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => remove(v)}><Trash2 className="mr-1 h-3.5 w-3.5" />Elimina utente</Button>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Policies() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/policies").then(({ data }) => setD(data));
  useEffect(() => {
    load();
  }, []);
  if (!d) return null;
  const toggleLevel = (p, l) => {
    const levels = p.levels.includes(l) ? p.levels.filter((x) => x !== l) : [...p.levels, l];
    const { key, ...body } = { ...p, levels };
    run(() => api.put(`/admin/policies/${key}`, body), "Requisiti aggiornati", load);
  };
  return (
    <div className="space-y-6" data-testid="policies-panel">
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50 text-slate-500"><tr><th className="px-3 py-2">Servizio</th><th className="px-3 py-2">Permesso</th><th className="px-3 py-2">Ruoli</th><th className="px-3 py-2">Livelli richiesti</th><th className="px-3 py-2">Consensi</th></tr></thead>
          <tbody>
            {d.policies.map((p) => (
              <tr key={p.key} className="border-t border-slate-100" data-testid={`policy-${p.key}`}>
                <td className="px-3 py-2"><b>{p.key}</b><br />{p.label}</td>
                <td className="px-3 py-2">{p.public ? "pubblico" : p.permission}</td>
                <td className="px-3 py-2">{p.roles.join(", ") || "tutti"}</td>
                <td className="px-3 py-2">{!p.public && LV.map((l) => <button key={l} data-testid={`policy-${p.key}-${l}`} onClick={() => toggleLevel(p, l)} className={`mr-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${p.levels.includes(l) ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-400"}`}>{l}</button>)}</td>
                <td className="px-3 py-2">{p.consents.join(", ") || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {d.role_permissions.map((rp) => (
          <div key={rp.role} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`role-perms-${rp.role}`}>
            <p className="mb-2 text-sm font-bold">{ROLE_LABELS[rp.role]}</p>
            <div className="flex flex-wrap gap-1">
              {Object.entries(d.permissions).map(([k, l]) => {
                const on = rp.permissions.includes(k);
                const next = on ? rp.permissions.filter((x) => x !== k) : [...rp.permissions, k];
                return <button key={k} title={l} onClick={() => run(() => api.put(`/admin/role-permissions/${rp.role}`, next), "Permessi aggiornati", load)} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${on ? "bg-cyan-100 text-cyan-800" : "bg-slate-100 text-slate-400"}`}>{k}</button>;
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
